// AdminOverview — admin sub-view, extracted verbatim from App.js.
import { useMemo } from "react";
import { useT } from "../lib/i18n";
import { C } from "../lib/theme";
import { fmt, pctR, posc, curSym } from "../lib/format";
import { Card, StatCard, SectionHdr, ChartTip } from "../components/ui";
import { calcDaily, calcBreakEvenFromRows, applyPeriodAdjustments, monthsForSpan } from "../lib/calc";
import { effNet } from "../lib/coFund";
import { filterHistory, rangeWindow } from "../lib/ranges";
import { bucketRowsForChart } from "../lib/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

export function AdminOverview({ws, cfg, chId, range="ALL", setRange, customFrom="", setCustomFrom, customTo="", setCustomTo, onNavigate}) {
  const { t } = useT();

  // Filter history to selected channel only when chId is set, else all channels combined
  const sourceHistory = useMemo(()=>{
    // Build a channel lookup so we can attach channel-specific rates (commission, cardFeePct) to each row
    const channelLookup = ws?.channels || {};
    const enrichRow = (r) => {
      const ch = r.channelId ? channelLookup[r.channelId] : null;
      return {
        ...r,
        // Attach channel rates if not already on the row — these are used by calcDaily to apply correct commission/card fee
        commission: r.commission ?? ch?.commission,
        cardFeePct: r.cardFeePct ?? ch?.cardFeePct,
      };
    };
    if(chId && ws?.channels?.[chId]) return (ws.channels[chId].history||[]).map(enrichRow);
    return (ws?.history||[]).map(enrichRow);
  },[ws,chId]);
  const allRowsCalc = useMemo(()=>sourceHistory.map(r=>({...r,...calcDaily(r.gross,effNet(r),r.cpc,{aggCommission:r.commission ?? cfg.aggCommission,cardFee:r.cardFeePct ?? cfg.cardFee,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true})})),[sourceHistory,cfg,ws]);

  // Apply the range filter
  const filtered = useMemo(()=>filterHistory(allRowsCalc, range, customFrom, customTo), [allRowsCalc, range, customFrom, customTo]);

  // YoY comparison only available when viewing all data
  const showYoY = range === "ALL";
  const cur  = showYoY ? allRowsCalc.filter(r=>r.dateObj instanceof Date && r.dateObj.getFullYear()===2026) : filtered;
  const prev = showYoY ? allRowsCalc.filter(r=>r.dateObj instanceof Date && r.dateObj.getFullYear()===2025) : [];
  // Sum the per-row calcDaily fields using the central function's field names so
  // applyPeriodAdjustments can roll them up. (`net` for display is mapped from
  // `netReceivable` after the call.)
  const sum  = arr=>arr.reduce((a,r)=>({
    gross:a.gross+r.gross,
    netReceivable:a.netReceivable+r.netReceivable,
    cpc:a.cpc+(r.cpc||0),
    cogs:a.cogs+r.cogs,
    vatOnComm:a.vatOnComm+(r.vatOnComm||0),
    vatOnCard:a.vatOnCard+(r.vatOnCard||0),
    vatOnCpc:a.vatOnCpc+(r.vatOnCpc||0),
  }),{gross:0,netReceivable:0,cpc:0,cogs:0,vatOnComm:0,vatOnCard:0,vatOnCpc:0});
  const c=sum(cur), p=sum(prev);

  // ── Bring the KPI totals up to the Insight calc ──────────────────────
  // The per-row sums above DON'T match the Insight totals until the period-level
  // adjustments are applied: (1) CPC topped up to the full committed period
  // spend, (2) the per-aggregator subscription fee deducted, (3) output VAT
  // recomputed on the FINAL net receivable, and (4) earnings-after-VAT + true
  // profit derived. This now goes through the SINGLE central applyPeriodAdjustments
  // (calc.js) so Overview ties out with the Insight / Daily Log. calcDaily untouched.
  const vatRateNow = ws?.vatRate ?? 0;
  // CPC scope = selected channel or all channels; collect their allocations,
  // tagged with the owning channel's id (same as the Insight / Daily Log).
  const scopeChannels = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels || {});
  const scopeAllocs   = scopeChannels.flatMap(ch => (ch.cpcAllocations || []).map(a => ({ ...a, channelId: a.channelId ?? ch.id })));
  // Window derivation — the SHARED helper from lib/ranges (one formula for
  // row-selection and the CPC window alike). The CPC window is capped at today
  // (the P&L counts CPC only for current+past days, never future days of a
  // range). ALL → unbounded (winFrom null = full entered amounts).
  const windowFor = (rng) => {
    const { winFrom, winTo } = rangeWindow(rng, customFrom, customTo);
    return { winFrom, winTo };
  };
  // Inclusive day-span of the rows (or 30 if none) — for the subscription fee.
  const spanDays = (arr) => {
    const dts = arr.map(r => r.dateObj?.getTime()).filter(x => !isNaN(x));
    if (dts.length === 0) return 30;
    return Math.max(1, Math.round((Math.max(...dts) - Math.min(...dts)) / 86400000) + 1);
  };
  // Apply the period-level ladder (CPC top-up + subscription + output-VAT +
  // EAV + true profit) to a sum'd total `tot` via the SINGLE central function,
  // then map netReceivable → the `net` field the KPI cards render.
  const applyInsightTotals = (tot, rng, rows) => {
    const { winFrom, winTo } = windowFor(rng);
    applyPeriodAdjustments(tot, {
      allocs: scopeAllocs, winFrom, winTo, vatRate: vatRateNow,
      includeSubscription: true, subChannels: scopeChannels, subPeriodDays: spanDays(rows),
    });
    tot.net = tot.netReceivable;
    return tot;
  };
  // CURRENT (displayed) total: when YoY (range==="ALL") cur = 2026 rows scoped to
  // the "ALL" window (unbounded), else cur = filtered for the selected range.
  applyInsightTotals(c, showYoY ? "ALL" : range, cur);
  // PREVIOUS (2025, YoY only): run the SAME central ladder for parity. CPC is the
  // per-row sum because a precise 2025-only CPC window can't be derived from the
  // shared range controls (winTo is capped at today, which would miss 2025) — so
  // we pass NO allocs (the top-up nets to zero against the per-row CPC). prev is
  // the small "vs 2025" delta label only, not a headline KPI.
  if (showYoY) {
    const prevCpc = +(prev.reduce((a,r)=>a+(r.cpc||0),0)).toFixed(2);
    // No allocs + a zero CPC baseline → extraCpc = 0, so the central function makes
    // NO CPC top-up (netReceivable keeps the per-row CPC deduction already inside
    // calcDaily) — exactly the prior prev-only behavior.
    p.cpc = 0;
    applyPeriodAdjustments(p, {
      allocs: [], winFrom: null, winTo: null, vatRate: vatRateNow,
      includeSubscription: true, subChannels: scopeChannels, subPeriodDays: spanDays(prev),
    });
    p.cpc = prevCpc; // restore the informational 2025 per-row CPC actuals
    p.net = p.netReceivable;
  }
  const yoy=f=>showYoY && p[f]>0?`${(((c[f]-p[f])/p[f])*100).toFixed(1)}% ${t("overview.kpi.vs2025")}`:"";
  const filterLabel = chId && ws?.channels?.[chId] ? ws.channels[chId].platform : t("overview.filter.allPlatforms");

  // Period label for KPI cards
  const periodPrefix = range === "ALL" ? t("overview.period.ytd") : range === "1D" ? t("overview.period.today") : range === "CUSTOM" && customFrom && customTo ? t("overview.period.custom") : range;
  const periodSubtitle = range === "CUSTOM" && customFrom && customTo
    ? `${ws.name} · ${filterLabel} · ${customFrom} → ${customTo}`
    : range === "ALL"
      ? `${ws.name} · ${filterLabel} · ${t("overview.period.ytd2026")}`
      : range === "1D"
        ? `${ws.name} · ${filterLabel} · ${t("overview.period.today")}`
        : `${ws.name} · ${filterLabel} · ${t("overview.period.lastRange")} ${range}`;

  // Bucket the filtered rows by day (short ranges) or month (longer) for the
  // profit bar chart, with readable dates on the x-axis.
  const profitChart = useMemo(() => bucketRowsForChart(range === "ALL" ? allRowsCalc : filtered), [range, allRowsCalc, filtered]);
  const chartData = profitChart.data;
  const chartMode = profitChart.mode;
  // Human date range for the chart title
  const chartRangeLabel = (() => {
    if (chartData.length === 0) return "";
    const first = chartData[0].label, last = chartData[chartData.length-1].label;
    return first === last ? first : `${first} → ${last}`;
  })();

  return (
    <div style={{padding:24}}>
      <SectionHdr
        title={t("overview.title")}
        sub={periodSubtitle}
      />
      {/* Platform contributions failed to load (db.js sets ws.coFundLoadFailed):
          every figure below falls back to the raw net, so say so. */}
      {ws?.coFundLoadFailed === true && (
        <div role="status" style={{padding:"10px 14px",marginBottom:16,background:C.yellow10,border:`1px solid ${C.yellow}55`,borderLeft:`3px solid ${C.yellow}`,borderRadius:7,fontSize:12.5,color:"#7a4f00",lineHeight:1.55,fontWeight:500}}>
          {t("overview.warn.coFundLoadFailed")}
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        <StatCard label={`${periodPrefix} ${t("overview.kpi.gross")}`}      value={fmt(c.gross)}  accent={C.blue}         color={C.text} sub={yoy("gross")}/>
        <StatCard label={`${periodPrefix} ${t("overview.kpi.netRecv")}`}  value={fmt(c.net)}    accent={C.blue}         color={C.blue} sub={yoy("net")}/>
        <StatCard label={`${periodPrefix} ${t("overview.kpi.vatPaid")}`}   value={fmt(c.vat)}    accent={C.red}          color={C.red}/>
        <StatCard label={`${periodPrefix} ${t("overview.kpi.profitPreFixed")}`} value={fmt(c.profit)} accent={posc(c.profit)} color={posc(c.profit)} sub={yoy("profit")}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16}}>
        <Card>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4,flexWrap:"wrap",gap:6}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{chartMode==="day"?t("overview.chart.dailyTrueProfit"):t("overview.chart.monthlyTrueProfit")}</div>
            {chartRangeLabel && <div style={{fontSize:11.5,color:C.muted2,fontWeight:600}}>{chartRangeLabel}</div>}
          </div>
          <div style={{fontSize:11.5,color:C.muted,marginBottom:14,fontWeight:500}}>{t("overview.chart.trueProfit")} {chartMode==="day"?t("overview.chart.perDay"):t("overview.chart.perMonth")} · {filterLabel}</div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={chartData} margin={{top:4,right:4,left:-4,bottom:0}} barSize={chartData.length>20?6:chartData.length>10?12:18}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border2} vertical={false}/>
              <XAxis dataKey="shortLabel" tick={{fontSize:9,fill:C.muted2}} tickLine={false} axisLine={{stroke:C.border}} interval={chartData.length>16?Math.floor(chartData.length/8):0} angle={chartData.length>10?-30:0} textAnchor={chartData.length>10?"end":"middle"} height={chartData.length>10?40:24}/>
              <YAxis tick={{fontSize:9,fill:C.muted2}} tickLine={false} axisLine={false} width={48} tickFormatter={v=>`${curSym()}${Math.abs(v)>=1000?(v/1000).toFixed(1)+"k":v.toFixed(0)}`}/>
              <Tooltip content={<ChartTip/>} cursor={{fill:"rgba(59,91,219,0.06)"}}/>
              <ReferenceLine y={0} stroke={C.border}/>
              <Bar dataKey="trueProfit" name={t("overview.chart.trueProfitLegend")} fill={C.blue} radius={[2,2,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14,letterSpacing:"-.2px"}}>{t("overview.rates.title")}</div>
          {[
            [t("overview.rates.vatRate"), pctR(ws?.vatRate ?? cfg.vatRate), C.redDark],
            [t("overview.rates.cogs"),     pctR(ws?.cogsPct ?? cfg.cogsPct), C.muted],
            ...(chId && ws?.channels?.[chId] ? [
              [`${ws.channels[chId].platform} ${t("overview.rates.commSuffix")}`, pctR(ws.channels[chId].commission), C.redDark],
              [`${ws.channels[chId].platform} ${t("overview.rates.cardFeeSuffix")}`, pctR(ws.channels[chId].cardFeePct), C.muted],
            ] : [[t("overview.rates.commission"), t("overview.rates.seeChannel"), C.muted2], [t("overview.rates.cardFee"), t("overview.rates.seeChannel"), C.muted2]]),
          ].map(([l,v,col])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border2}`}}>
              <span style={{fontSize:12.5,color:C.muted,fontWeight:500}}>{l}</span>
              <span className="num" style={{fontSize:13,fontWeight:700,color:col}}>{v}</span>
            </div>
          ))}
          <button onClick={()=>onNavigate && onNavigate("config")} style={{marginTop:12,padding:"8px 12px",background:C.yellow10,border:`1px solid ${C.yellow}33`,borderRadius:7,fontSize:11,color:"#7a4f00",fontWeight:600,cursor:"pointer",fontFamily:"inherit",width:"100%",textAlign:"left"}}>{t("overview.rates.editInConfig")}</button>
        </Card>
      </div>

      {/* Contribution margin */}
      {filtered.length > 0 && (()=>{
        const totalGross = filtered.reduce((a,r)=>a+(r.gross||0),0);
        const totalEAV   = filtered.reduce((a,r)=>a+(r.earningAfterVat||0),0);
        const totalCOGS  = filtered.reduce((a,r)=>a+(r.cogs||0),0);
        const contrib    = totalEAV - totalCOGS;
        const contribPct = totalGross>0 ? contrib/totalGross : 0;

        // Calculate actual after-fixed-costs profit by allocating fixed costs to the period
        // When viewing a single channel, allocate fixed costs proportionally based on
        // that channel's share of total workspace gross over the same date range.
        const fcOv = ws?.fixedCosts || {rent:0,wages:0,utilities:0,other:0};
        const workspaceFixed = (fcOv.rent||0)+(fcOv.wages||0)+(fcOv.utilities||0)+(fcOv.other||0);
        const totalFixedMonthly = (() => {
          if (!chId) return workspaceFixed;
          if (workspaceFixed === 0) return 0;
          // Sum gross across ALL channels for this same date range
          const allWsRows = Object.values(ws?.channels || {}).flatMap(c => {
            const rows = (c.history || []).map(r=>({...r}));
            return filterHistory(rows, range, customFrom, customTo);
          });
          const totalWsGross = allWsRows.reduce((s, r) => s + (r.gross || 0), 0);
          if (totalWsGross === 0) return workspaceFixed;
          const channelGross = filtered.reduce((s, r) => s + (r.gross || 0), 0);
          return workspaceFixed * (channelGross / totalWsGross);
        })();
        const periodDays = (()=>{
          if(filtered.length === 0) return 30;
          const dates = filtered.map(r=>r.dateObj?.getTime()).filter(t=>!isNaN(t));
          if(dates.length === 0) return 30;
          const span = (Math.max(...dates) - Math.min(...dates))/86400000 + 1;
          return Math.max(1, Math.round(span));
        })();
        const { winFrom: fixWinFrom, winTo: fixWinTo } = rangeWindow(range, customFrom, customTo);
        const periodFixed = totalFixedMonthly * ((fixWinFrom && fixWinTo) ? monthsForSpan(fixWinFrom, fixWinTo) : periodDays / 30.4375);
        const trueProfitAfterFixed = contrib - periodFixed;
        const healthy = contribPct >= 0.25;
        return (
          <Card sx={{marginTop:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{t("overview.earnings.title")}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:4,fontWeight:500}}>{t("overview.earnings.desc")}</div>
              </div>
              <span style={{padding:"6px 13px",borderRadius:20,fontSize:11.5,fontWeight:700,background:healthy?C.green10:C.yellow10,color:healthy?C.greenDark:"#7a4f00",border:`1px solid ${healthy?C.green+"33":C.yellow+"33"}`,letterSpacing:"-.1px"}}>
                {healthy ? t("overview.earnings.healthy") : t("overview.earnings.below25")}
              </span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:posc(contrib)}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.earnings.profit")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:posc(contrib),letterSpacing:"-.5px",lineHeight:1.1}}>{fmt(contrib)}</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{t("overview.earnings.beforeFixedOverheads")}</div>
              </div>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:healthy?C.green:C.yellow}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.earnings.marginPct")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:healthy?C.greenDark:"#7a4f00",letterSpacing:"-.5px",lineHeight:1.1}}>{(contribPct*100).toFixed(1)}%</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{t("overview.earnings.ofGrossSales")}</div>
              </div>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:posc(trueProfitAfterFixed)}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.earnings.trueProfit")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:posc(trueProfitAfterFixed),letterSpacing:"-.5px",lineHeight:1.1}}>{fmt(trueProfitAfterFixed)}</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{totalFixedMonthly > 0 ? `${fmt(periodFixed)} ${t("overview.earnings.fixedDeducted")} (${fmt(totalFixedMonthly)}${t("overview.earnings.perMo")}${chId ? ` ${t("overview.earnings.allocated")}` : ""} × ${periodDays}${t("overview.earnings.daysAbbrev")})` : t("overview.earnings.noFixedCosts")}</div>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Break-even analysis */}
      {(()=>{
        const fc = ws?.fixedCosts || {rent:0,wages:0,utilities:0,other:0};
        const totalFixed = (fc.rent||0)+(fc.wages||0)+(fc.utilities||0)+(fc.other||0);
        if(totalFixed===0) return (
          <Card sx={{marginTop:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:6}}>{t("overview.breakeven.title")}</div>
            <div style={{fontSize:12,color:C.muted}}>{t("overview.breakeven.setFixedCostsPre")}<span onClick={()=>onNavigate && onNavigate("config")} style={{color:C.blue,fontWeight:600,cursor:"pointer",textDecoration:"underline"}}>{t("overview.breakeven.configLink")}</span>{t("overview.breakeven.setFixedCostsPost")}</div>
          </Card>
        );
        // Period days = actual span between earliest and latest entry
        const periodDays = (()=>{
          if(filtered.length === 0) return 30;
          const dates = filtered.map(r=>r.dateObj.getTime()).filter(t=>!isNaN(t));
          if(dates.length === 0) return 30;
          const span = (Math.max(...dates) - Math.min(...dates))/86400000 + 1;
          return Math.max(1, Math.round(span));
        })();
        const be = calcBreakEvenFromRows(filtered, fc, periodDays);
        const above = be.avgDailyGross >= be.breakEvenDailyGross;
        return (
          <Card sx={{marginTop:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{t("overview.breakeven.title")}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:4,fontWeight:500}}>{filterLabel} · {periodDays} {t("overview.breakeven.days")} · {fmt(totalFixed)}{t("overview.breakeven.fixedCostsPerMo")}</div>
              </div>
              <span style={{padding:"6px 13px",borderRadius:20,fontSize:11.5,fontWeight:700,background:above?C.green10:C.red10,color:above?C.greenDark:C.redDark,border:`1px solid ${(above?C.green:C.red)}33`,letterSpacing:"-.1px"}}>
                {above?t("overview.breakeven.above"):t("overview.breakeven.below")}
              </span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:C.muted2}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.breakeven.dailyFixed")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:C.text,letterSpacing:"-.5px",lineHeight:1.1}}>{fmt(be.dailyFixed)}</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{t("overview.breakeven.perDay")}</div>
              </div>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:C.blue}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.breakeven.breakEvenGross")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:C.blueDark,letterSpacing:"-.5px",lineHeight:1.1}}>{fmt(be.breakEvenDailyGross)}</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{t("overview.breakeven.targetPerDay")}</div>
              </div>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:posc(be.avgDailyGross-be.breakEvenDailyGross)}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.breakeven.avgDailyGross")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:posc(be.avgDailyGross-be.breakEvenDailyGross),letterSpacing:"-.5px",lineHeight:1.1}}>{fmt(be.avgDailyGross)}</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{t("overview.breakeven.actual")}</div>
              </div>
              <div style={{padding:"14px 16px",background:C.bg3,borderRadius:10,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:posc(be.profitAfterFixed)}}/>
                <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:8,marginTop:2}}>{t("overview.breakeven.coverage")}</div>
                <div className="num" style={{fontSize:21,fontWeight:800,color:posc(be.profitAfterFixed),letterSpacing:"-.5px",lineHeight:1.1}}>{be.coveragePercent}%</div>
                <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{be.profitAfterFixed>=0?"+":""}{fmt(be.profitAfterFixed)} {t("overview.breakeven.afterFixed")}</div>
              </div>
            </div>
          </Card>
        );
      })()}
    </div>
  );
}
